export const LOGO_SIZE = 800
export const SOURCE_LIMIT = 20 * 1024 * 1024

export function canvasFor(width, height) {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  return canvas
}

export function sanitizeSvg(text) {
  if (/<!DOCTYPE|<!ENTITY/i.test(text)) throw new Error("Please export this SVG without external entities.")
  const doc = new DOMParser().parseFromString(text, "image/svg+xml")
  const root = doc.documentElement
  if (doc.querySelector("parsererror") || root.localName !== "svg") throw new Error("This SVG could not be read.")
  // SVG is decoded only as an image, never inserted as markup. Also reject
  // active content and references so previews cannot fetch external resources.
  for (const node of doc.querySelectorAll("*")) {
    if (["script", "foreignObject", "iframe", "object", "embed", "image", "animate", "set", "animateTransform"].includes(node.localName)) {
      throw new Error("This SVG contains embedded content. Please export a self-contained SVG or PNG.")
    }
    for (const attr of node.attributes) {
      if (/^on/i.test(attr.name) || (attr.localName === "href" && !attr.value.trim().startsWith("#"))) {
        throw new Error("This SVG contains external or interactive content. Please export a plain SVG or PNG.")
      }
    }
  }
  const values = root.textContent + " " + Array.from(doc.querySelectorAll("*")).flatMap(n => Array.from(n.attributes).filter(a => a.name !== "xmlns" && !a.name.startsWith("xmlns:")).map(a => a.value)).join(" ")
  if (/@import|(?:https?:|file:|data:|javascript:)/i.test(values) || Array.from(values.matchAll(/url\(([^)]*)\)/gi)).some(match => !match[1].trim().replace(/^["']|["']$/g, "").startsWith("#"))) {
    throw new Error("This SVG references external content. Please export a self-contained SVG or PNG.")
  }
  const viewBox = root.getAttribute("viewBox")?.trim().split(/[ ,]+/).map(Number)
  const width = viewBox?.length === 4 ? viewBox[2] : parseFloat(root.getAttribute("width"))
  const height = viewBox?.length === 4 ? viewBox[3] : parseFloat(root.getAttribute("height"))
  if (!(width > 0 && height > 0 && Number.isFinite(width + height))) throw new Error("This SVG needs a valid viewBox or width and height.")
  const scale = 1600 / Math.max(width, height)
  root.setAttribute("width", Math.max(1, Math.round(width * scale)))
  root.setAttribute("height", Math.max(1, Math.round(height * scale)))
  if (!root.hasAttribute("viewBox")) root.setAttribute("viewBox", `0 0 ${width} ${height}`)
  return new Blob([new XMLSerializer().serializeToString(root)], {type: "image/svg+xml"})
}

export async function readRaster(file) {
  const objectUrl = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.src = objectUrl
    await image.decode()
    if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 48_000_000) {
      throw new Error("This image is too large to preview. Please export it below 48 megapixels.")
    }
    const scale = Math.min(1, 2000 / Math.max(image.naturalWidth, image.naturalHeight))
    const canvas = canvasFor(Math.max(1, Math.round(image.naturalWidth * scale)), Math.max(1, Math.round(image.naturalHeight * scale)))
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height)
    return canvas
  } finally { URL.revokeObjectURL(objectUrl) }
}

export function artworkBounds(canvas) {
  const {width, height} = canvas
  const {data} = canvas.getContext("2d", {willReadFrequently: true}).getImageData(0, 0, width, height)
  const white = i => data[i + 3] > 248 && data[i] > 248 && data[i + 1] > 248 && data[i + 2] > 248
  const whiteBackground = [0, (width - 1) * 4, (height - 1) * width * 4, (width * height - 1) * 4].every(white)
  let left = width, top = height, right = -1, bottom = -1
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      if (data[i + 3] < 8 || (whiteBackground && white(i))) continue
      left = Math.min(left, x); right = Math.max(right, x)
      top = Math.min(top, y); bottom = Math.max(bottom, y)
    }
  }
  if (right < left) return {x: 0, y: 0, width, height}
  // A small buffer protects antialiased edges; admins can turn trimming off.
  left = Math.max(0, left - 2); top = Math.max(0, top - 2)
  return {x: left, y: top, width: Math.min(width - left, right - left + 3), height: Math.min(height - top, bottom - top + 3)}
}

export function fitLogo(source, target, {trim, padding, background}) {
  const bounds = trim ? artworkBounds(source) : {x: 0, y: 0, width: source.width, height: source.height}
  const ctx = target.getContext("2d")
  ctx.clearRect(0, 0, LOGO_SIZE, LOGO_SIZE)
  if (background === "white") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, LOGO_SIZE, LOGO_SIZE) }
  const available = LOGO_SIZE * (1 - 2 * padding / 100)
  const scale = Math.min(available / bounds.width, available / bounds.height)
  const width = bounds.width * scale, height = bounds.height * scale
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high"
  ctx.drawImage(source, bounds.x, bounds.y, bounds.width, bounds.height, (LOGO_SIZE - width) / 2, (LOGO_SIZE - height) / 2, width, height)
  return bounds
}
